import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'next-i18next';
import { useSelector } from 'react-redux';
import { capitalizeWords } from '@utils/gobal';
import { mediaBreakpoint, RESPONSIVE, useResponsive } from '@services/responsiveProvider';
import { selectEventCategoriesIds } from '@features/eventDetail/slices/eventDetailServerSlice';
import { textH3, textH4, textSmall } from '@features/ui/styles/textStyles';
import { table } from '@features/eventDetail/components/EventCategoryList/styles';
import CategoryLine from '@features/eventDetail/components/EventCategoryList/CategoryLine';
import { selectCurrentUserDetail, selectIsAuthenticated } from '@features/users/slices/userSlice';
import { selectSubscriptionFor, selectSubscriptionPack } from '@features/subscriptions/slices/formSubscriptionSlice';

interface EventCategoryListProps {
  eventId: number;
}

const TableHeader = () => {
  const { t } = useTranslation();
  return (
    <TableHeaderContainer>
      <div>{t('eventCategory_tableHeaderTitle.message')}</div>
      <div>{t('eventCategory_tableHeaderGender.message')}</div>
      <div className="tableHeader_align-right">{t('eventCategory_tableHeaderAgeRange.message')}</div>
      <div>{t('eventCategory_tableHeaderDistance.message')}</div>
      <div className="tableHeader_align-right">{t('eventCategory_tableHeaderPrice.message')}</div>
    </TableHeaderContainer>
  );
};

const EventCategoryList = ({ eventId }: EventCategoryListProps) => {
  const { t } = useTranslation();
  const responsive = useResponsive();

  const categories = useSelector(selectEventCategoriesIds(eventId));
  const logged = useSelector(selectIsAuthenticated);
  const subscriptionFor = useSelector(selectSubscriptionFor);
  const user = useSelector(selectCurrentUserDetail);
  const pack = useSelector(selectSubscriptionPack);

  const userSelected = subscriptionFor ?? user;

  const categoryTitle = logged ? t(`eventSubscription_categoryTitleLogged_${userSelected.gender}.message`, {
    count: categories.length,
    firstName: capitalizeWords(userSelected.firstName),
    lastName: capitalizeWords(userSelected.lastName),
    birthYear: userSelected.birthDateObj.year,
  }) : t('eventSubscription_categoryTitle.message', { count: categories.length });

  return (
    <>
      {categories.length > 0 && !pack && (
        <CategoryTitle>
          {categoryTitle}
        </CategoryTitle>
      )}
      <Container>
        {categories.length > 0 && (
          <>
            {responsive !== RESPONSIVE.MOBILE && <TableHeader />}
            {categories.map(categoryId => (
              <CategoryLine key={categoryId} eventId={eventId} categoryId={categoryId} />
            ))}
          </>
        )}
        {categories.length === 0 && (
          <NoData>{t('EventCategoryList_noData.message')}</NoData>
        )}
      </Container>
    </>
  );
};

export default EventCategoryList;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;
const NoData = styled.div`
  display: flex;
  justify-content: center;
  ${textH4};
`;
const CategoryTitle = styled.h3`
  ${textH3};
  margin-top: 20px;
`;

const TableHeaderContainer = styled.div`
  ${table};
  --table-header-margin-left: 60px;
  --table-header-margin-top: 20px;
  --table-header-margin-right: 20px;
  --table-header-margin-bottom: 10px;
  /* makes sure the width of the header is the same as the category line header */
  width: calc(100% - var(--table-header-margin-left) - var(--table-header-margin-right));
  margin-left: var(--table-header-margin-left);
  margin-top: var(--table-header-margin-top);
  margin-bottom: var(--table-header-margin-bottom);
  color: var(--color-grey3);
  ${textSmall};
  text-transform: uppercase;

  > div {
    max-width: 100px;
  }

  .tableHeader_align-right {
    text-align: right;
  }

  ${mediaBreakpoint.mobile} {
    > div {
      max-width: unset;
    }

    .tableHeader_align-right {
      text-align: unset;
    }
  }
`;
